import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import { Mic, Image, Video, Music, FileText, MapPin, Contact, UserRound, Sparkles } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { cn } from "../../lib/utils";
import ContactAvatar from "../ContactAvatar";
import ChannelIcon from "../ChannelIcon";

const renderLastMessagePreview = (lastMessage) => {
  if (!lastMessage) return null;
  const msg = lastMessage.trim();

  const mediaPreview = (Icon, label) => (
    <span className="flex items-center gap-1 text-slate-400">
      <Icon className="w-3 h-3" />
      <span>{label}</span>
    </span>
  );

  if (msg.startsWith("BEGIN:VCARD")) {
    const fnMatch = msg.match(/^FN[^:]*:(.+)$/m);
    return mediaPreview(Contact, fnMatch ? fnMatch[1].trim() : "Contacto");
  }
  if (/\.(ogg|oga|opus)$/i.test(msg) || msg === "ptt")
    return mediaPreview(Mic, "Nota de voz");
  if (/\.(jpe?g|png|gif|webp)$/i.test(msg))
    return mediaPreview(Image, "Imagen");
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(msg))
    return mediaPreview(Video, "Video");
  if (/\.(mp3|wav|aac|m4a|flac)$/i.test(msg))
    return mediaPreview(Music, "Audio");
  if (msg.startsWith("data:image/png;base64,"))
    return mediaPreview(MapPin, "Ubicación");
  if (/\.[a-z0-9]{2,5}$/i.test(msg) && !msg.includes(" ") && msg.length < 80)
    return mediaPreview(FileText, "Archivo");

  // disableLinks: a click on the preview must open the conversation, never
  // navigate to a URL the contact sent.
  return <MarkdownWrapper disableLinks>{msg}</MarkdownWrapper>;
};

const TicketListItem = ({ ticket, aiTyping }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const [draftText, setDraftText] = useState("");

  const isSelected = ticketId && +ticketId === ticket.id;
  const isPending = ticket.status === "pending";

  // Indicador de "lo tiene otro agente": se muestra cuando el ticket está
  // asignado a un usuario distinto del actual. `ticket.user` viene incluido
  // desde ListTicketsService.
  const assignedToOther = ticket.userId && ticket.userId !== user?.id;
  const assignedName = ticket.user?.name;

  useEffect(() => {
    const checkDraft = () => {
      setDraftText(localStorage.getItem(`draft_${ticket.id}`) || "");
    };
    checkDraft();
    const interval = setInterval(checkDraft, 1000);
    const handler = (e) => {
      if (e.key === `draft_${ticket.id}`) checkDraft();
    };
    window.addEventListener("storage", handler);
    return () => {
      isMounted.current = false;
      window.removeEventListener("storage", handler);
      clearInterval(interval);
    };
  }, [ticket.id]);

  useEffect(() => () => { isMounted.current = false; }, []);

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, { status: "open", userId: user?.id });
      if (isMounted.current) setLoading(false);
      history.push(`/tickets/${id}`);
    } catch (err) {
      if (isMounted.current) setLoading(false);
      toastError(err);
    }
  };

  const handleSelectTicket = (id) => {
    history.push(`/tickets/${id}`);
  };

  const timeStr = ticket.lastMessage
    ? isSameDay(parseISO(ticket.updatedAt), new Date())
      ? format(parseISO(ticket.updatedAt), "HH:mm")
      : format(parseISO(ticket.updatedAt), "dd/MM")
    : "";

  return (
    <div className="relative">
      {/* Queue color bar */}
      <div
        className="absolute left-0 top-0 bottom-0 w-0.5 rounded-r"
        style={{ backgroundColor: ticket.queue?.color || "transparent" }}
        title={ticket.queue?.name || "Sin cola"}
      />

      <button
        onClick={() => {
          if (isPending) return;
          handleSelectTicket(ticket.id);
        }}
        className={cn(
          "w-full flex items-start gap-3 px-4 py-3 pl-3 text-left transition-colors relative",
          isSelected
            ? "bg-indigo-50 dark:bg-indigo-950/50 border-l-2 border-indigo-500"
            : "hover:bg-slate-50 dark:hover:bg-slate-800/50 border-l-2 border-transparent",
          isPending && "bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100/50"
        )}
      >
        {/* Avatar */}
        <div className="flex-shrink-0 mt-0.5">
          <ContactAvatar
            src={ticket.contact?.profilePicUrl}
            alt={ticket.contact?.name}
            className="w-10 h-10 rounded-full object-cover"
            size="md"
            contactId={ticket.contact?.id}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
              {ticket.contact?.name}
            </span>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {ticket.status === "closed" && (
                <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                  Cerrado
                </span>
              )}
              {timeStr && (
                <span className="text-[11px] text-slate-400">{timeStr}</span>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mt-0.5 gap-2">
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate flex-1">
              {aiTyping ? (
                <span className="inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 font-medium">
                  <Sparkles className="w-3 h-3 animate-pulse" />
                  <span>IA respondiendo</span>
                  <span className="inline-flex gap-0.5">
                    <span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </span>
              ) : draftText ? (
                <span className="text-indigo-500 italic">Borrador: {draftText}</span>
              ) : (
                renderLastMessagePreview(ticket.lastMessage)
              )}
            </div>
            {ticket.unreadMessages > 0 && (
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">
                {ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {ticket.whatsappId && (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                <ChannelIcon channel={ticket.whatsapp?.channel || "whatsapp"} size={10} />
                {ticket.whatsapp?.name}
              </span>
            )}
            {assignedToOther && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 rounded px-1.5 py-0.5"
                title={`${i18n.t("ticketsList.assignedTo")} ${assignedName || ""}`.trim()}
              >
                <UserRound className="w-2.5 h-2.5" />
                {assignedName || i18n.t("ticketsList.assignedToOther")}
              </span>
            )}
          </div>
        </div>
      </button>

      {isPending && (
        <div className="px-4 pb-2">
          <ButtonWithSpinner
            loading={loading}
            onClick={() => handleAcepptTicket(ticket.id)}
            className="w-full text-xs py-1.5"
          >
            {i18n.t("ticketsList.buttons.accept")}
          </ButtonWithSpinner>
        </div>
      )}

      <div className="h-px bg-slate-100 dark:bg-slate-800 mx-4" />
    </div>
  );
};

export default TicketListItem;
